import React from "react";
import "./reportCard.css";
import { DEFAULT_LAYOUT, STUDENT_FIELDS } from "./layoutDefaults";

/**
 * Renders ONE report card from the builder JSON (live preview or published snapshot).
 * The order / visibility / headings of blocks come from report.layout (or the `layout` prop).
 * Pure layout – nothing is calculated here.
 */
const fmt = (v) => (v === null || v === undefined || v === "" ? "" : Number.isInteger(v) ? v : Math.round(v * 100) / 100);

// 90.01–100 -> "91-100", 0–32 -> "32 & Below"
const legendRange = (g) => (Number(g.from) <= 0 ? `${Math.floor(Number(g.to))} & Below` : `${Math.ceil(Number(g.from))}-${Math.floor(Number(g.to))}`);

/* ------------------------------------------------------------------ blocks */

const HeaderBlock = ({ report, school, settings }) => {
  const { student = {}, columns = [] } = report;
  const autoTitle = report.report_type === "EXAM_GROUP" ? `${columns[0]?.name || ""} Report Card` : "Annual Report Card";
  return (
    <div className="rc-head">
      {settings.show_logo && school.logo && (
        <img src={school.logo} alt="" className="rc-logo" onError={(e) => { e.target.style.display = "none"; }} />
      )}
      <div className="rc-head-text">
        {school.name && <div className="rc-school">{school.name}</div>}
        {settings.show_school_address && school.address && <div className="rc-address">{school.address}</div>}
        <div className="rc-title">
          {settings.title || autoTitle}{student.session_year ? ` · Session ${student.session_year}` : ""}
        </div>
      </div>
    </div>
  );
};

const StudentInfoBlock = ({ report, block }) => {
  const st = report.student || {};
  const values = {
    student_name: st.student_name,
    class: `${st.class_name || ""}${st.section_name ? ` - ${st.section_name}` : ""}`,
    father_name: st.father_name,
    mother_name: st.mother_name,
    sr_no: st.sr_no,
    roll_no: st.roll_no,
    date_of_birth: st.date_of_birth,
  };
  const keys = (block.fields && block.fields.length ? block.fields : DEFAULT_LAYOUT.blocks[1].fields)
    .filter((k) => STUDENT_FIELDS.some((f) => f.key === k));
  const pairs = keys.map((k) => [STUDENT_FIELDS.find((f) => f.key === k).label, values[k]]);
  const rows = [];
  for (let i = 0; i < pairs.length; i += 2) rows.push([pairs[i], pairs[i + 1]]);
  return (
    <>
      {block.title && <div className="rc-block-title">{block.title}</div>}
      <table className="rc-info">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <th>{r[0][0]}</th><td>{r[0][1]}</td>
              <th>{r[1]?.[0] || ""}</th><td>{r[1] ? r[1][1] : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const ScholasticBlock = ({ report, block }) => {
  const { columns = [], scholastic = {} } = report;
  const subjects = scholastic.subjects || [];
  const totals = scholastic.totals || { columns: {}, grand_total: {} };
  const showTP = block.show_theory_practical !== false;
  const showGT = block.show_grand_total !== false && columns.length > 1;

  const hasPart = (k, part) => subjects.some((s) => s.columns?.[k]?.[`has_${part}`]);

  /**
   * Sub-columns ("parts") of a term column. Every part prints Max + Obt.
   *  - term with several exams  -> one part per exam (Act.-I, Half Yearly …)
   *  - one exam, theory+practical -> Theory part + Practical part
   *  - one exam, one kind          -> no parts (Total says it all)
   */
  const partsOf = (c) => {
    if (c.exam_titles.length > 1) {
      return c.exam_titles.map((t) => ({
        id: t.id,
        label: t.name,
        cell: (x) => x?.titles?.[t.id]?.total,
        absent: (x) => x?.titles?.[t.id]?.absent,
        has: (x) => !!x?.titles?.[t.id],
        total: () => totals.columns?.[c.key]?.titles?.[t.id],
      }));
    }
    if (!showTP) return [];
    const kinds = ["theory", "practical"].filter((p) => hasPart(c.key, p));
    if (kinds.length < 2) return [];
    const title = c.exam_titles[0]?.name || "";
    return kinds.map((p) => ({
      id: p,
      label: `${title} ${p === "theory" ? "Theory" : "Practical"}`.trim(),
      cell: (x) => x?.[p],
      absent: () => false,
      has: (x) => !!x?.[`has_${p}`],
      total: () => {
        const r = subjects.reduce((a, s) => {
          const v = s.columns?.[c.key];
          if (v?.[`has_${p}`]) { a.max += v[p].max; a.obt += v[p].obt; }
          return a;
        }, { max: 0, obt: 0 });
        return r;
      },
    }));
  };
  const cols = columns.map((c) => ({ ...c, parts: partsOf(c) }));
  const totalLabel = (c) => (c.parts.length ? "Total" : c.exam_titles.length === 1 ? c.exam_titles[0].name : "Total");
  const obtCell = (v, absent) => (v ? (absent && !v.obt ? "AB" : fmt(v.obt)) : "");

  return (
    <>
      <table className="rc-grid rc-marks">
        <thead>
          <tr>
            <th rowSpan={3} className="rc-left rc-subject-head">{block.title || "Scholastic Area"}<br /><span className="rc-sub">Subject Name</span></th>
            {cols.map((c) => (
              <th key={c.key} colSpan={c.parts.length * 2 + 3} className="rc-gstart rc-term">{c.name}</th>
            ))}
            {showGT && <th colSpan={3} rowSpan={2} className="rc-gstart rc-term">Grand Total</th>}
          </tr>
          <tr>
            {cols.map((c) => (
              <React.Fragment key={c.key}>
                {c.parts.map((p, i) => <th key={p.id} colSpan={2} className={i === 0 ? "rc-gstart" : ""}>{p.label}</th>)}
                <th colSpan={2} className={c.parts.length ? "rc-strong" : "rc-gstart rc-strong"}>{totalLabel(c)}</th>
                <th rowSpan={2} className="rc-grade-head">Grade</th>
              </React.Fragment>
            ))}
          </tr>
          <tr>
            {cols.map((c) => (
              <React.Fragment key={c.key}>
                {c.parts.map((p, i) => (
                  <React.Fragment key={p.id}>
                    <th className={`rc-mo${i === 0 ? " rc-gstart" : ""}`}>Max</th>
                    <th className="rc-mo">Obt.</th>
                  </React.Fragment>
                ))}
                <th className={`rc-mo${c.parts.length ? "" : " rc-gstart"}`}>Max</th>
                <th className="rc-mo">Obt.</th>
              </React.Fragment>
            ))}
            {showGT && <><th className="rc-mo rc-gstart">Max</th><th className="rc-mo">Obt.</th><th className="rc-grade-head">Grade</th></>}
          </tr>
        </thead>
        <tbody>
          {subjects.map((s) => (
            <tr key={s.name}>
              <td className="rc-left rc-subject">{s.name}</td>
              {cols.map((c) => {
                const x = s.columns?.[c.key];
                return (
                  <React.Fragment key={c.key}>
                    {c.parts.map((p, i) => {
                      const v = p.has(x) ? p.cell(x) : null;
                      return (
                        <React.Fragment key={p.id}>
                          <td className={`rc-max${i === 0 ? " rc-gstart" : ""}`}>{v ? fmt(v.max) : "–"}</td>
                          <td>{v ? obtCell(v, p.absent(x)) : "–"}</td>
                        </React.Fragment>
                      );
                    })}
                    <td className={`rc-max${c.parts.length ? "" : " rc-gstart"}`}>{x ? fmt(x.total.max) : "–"}</td>
                    <td className="rc-strong">{x ? obtCell(x.total, x.absent) : "–"}</td>
                    <td className="rc-grade">{x?.grade || ""}</td>
                  </React.Fragment>
                );
              })}
              {showGT && (
                <>
                  <td className="rc-max rc-gstart">{s.grand_total ? fmt(s.grand_total.total.max) : ""}</td>
                  <td className="rc-strong">{s.grand_total ? fmt(s.grand_total.total.obt) : ""}</td>
                  <td className="rc-grade">{s.grand_total?.grade || ""}</td>
                </>
              )}
            </tr>
          ))}
          <tr className="rc-total">
            <td className="rc-left">Total</td>
            {cols.map((c) => {
              const t = totals.columns?.[c.key];
              return (
                <React.Fragment key={c.key}>
                  {c.parts.map((p, i) => {
                    const pt = p.total();
                    return (
                      <React.Fragment key={p.id}>
                        <td className={i === 0 ? "rc-gstart" : ""}>{pt ? fmt(pt.max) : ""}</td>
                        <td>{pt ? fmt(pt.obt) : ""}</td>
                      </React.Fragment>
                    );
                  })}
                  <td className={c.parts.length ? "" : "rc-gstart"}>{t ? fmt(t.max) : ""}</td>
                  <td>{t ? fmt(t.obt) : ""}</td>
                  <td className="rc-grade">{t?.grade || ""}</td>
                </React.Fragment>
              );
            })}
            {showGT && (
              <>
                <td className="rc-gstart">{fmt(totals.grand_total?.max)}</td>
                <td>{fmt(totals.grand_total?.obt)}</td>
                <td className="rc-grade">{totals.grand_total?.grade || ""}</td>
              </>
            )}
          </tr>
        </tbody>
      </table>

      {(block.show_percentage !== false || block.show_result !== false) && totals.grand_total?.percentage != null && (
        <div className="rc-summary-boxes">
          <div className="rc-box"><span>Marks Obtained</span><b>{fmt(totals.grand_total.obt)} / {fmt(totals.grand_total.max)}</b></div>
          {block.show_percentage !== false && <div className="rc-box"><span>Percentage</span><b>{fmt(totals.grand_total.percentage)}%</b></div>}
          {totals.grand_total.grade && <div className="rc-box"><span>Overall Grade</span><b>{totals.grand_total.grade}</b></div>}
          {block.show_result !== false && report.result_status && (
            <div className={`rc-box rc-result-${String(report.result_status).toLowerCase()}`}><span>Result</span><b>{report.result_status}</b></div>
          )}
        </div>
      )}
    </>
  );
};

const CoScholasticBlock = ({ report, block }) => {
  const co = report.co_scholastic || {};
  if (!(co.subjects || []).length) return null;
  return (
    <table className="rc-grid rc-cosch">
      <thead>
        <tr>
          <th className="rc-left">{block.title || "Co-Scholastic Area"}</th>
          {(co.columns || []).map((c) => <th key={c.key}>{c.name}<br />Grade</th>)}
        </tr>
      </thead>
      <tbody>
        {co.subjects.map((s) => (
          <tr key={s.id}>
            <td className="rc-left">{s.name}</td>
            {(co.columns || []).map((c) => <td key={c.key}><b>{s.grades?.[c.key] || ""}</b></td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const FooterBlock = ({ report, block }) => {
  const footer = report.footer || [];
  if (!footer.length) return null;
  return (
    <>
      {block.title && <div className="rc-block-title">{block.title}</div>}
      <table className="rc-footer">
        <tbody>
          {footer.map((f) => (
            <tr key={f.field_key}>
              <th>{f.title}</th>
              <td className={f.input_type === "TEXTAREA" ? "rc-remark" : ""}>{f.value ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const SignaturesBlock = ({ report, block }) => {
  const labels = (block.labels || []).filter((l) => l !== undefined);
  const date = report.issue_date ? new Date(report.issue_date).toLocaleDateString("en-GB") : "";
  return (
    <div className="rc-sign">
      {labels.map((l, i) => (
        <div key={i}>{/^date$/i.test(l.trim()) ? `Date: ${date}` : l}</div>
      ))}
    </div>
  );
};

const LegendBlock = ({ report, block }) => {
  const gl = report.grade_legend || {};
  const showS = block.show_scholastic !== false && (gl.scholastic || []).length > 0;
  const showC = block.show_co_scholastic !== false && (gl.co_scholastic || []).length > 0;
  if (!showS && !showC && !block.title) return null;
  return (
    <div className="rc-legend">
      {block.title && <div className="rc-legend-title">{block.title}</div>}
      <div className="rc-legend-tables">
        {showS && (
          <table>
            <thead><tr><th colSpan={2}>Scholastic</th></tr><tr><th>Marks Range</th><th>Grade</th></tr></thead>
            <tbody>{gl.scholastic.map((g) => <tr key={g.grade}><td>{legendRange(g)}</td><td>{g.grade}</td></tr>)}</tbody>
          </table>
        )}
        {showC && (
          <table>
            <thead><tr><th colSpan={2}>Co-Scholastic</th></tr><tr><th>Marks Range</th><th>Grade</th></tr></thead>
            <tbody>{gl.co_scholastic.map((g) => <tr key={g.grade}><td>{legendRange(g)}</td><td>{g.grade}</td></tr>)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const TextBlock = ({ block }) =>
  block.text ? (
    <div className="rc-text" style={{ textAlign: block.align || "left", fontWeight: block.bold ? 600 : 400 }}>
      {block.text}
    </div>
  ) : null;

const SpacerBlock = ({ block }) => <div style={{ height: `${block.height || 10}px` }} />;

const RENDERERS = {
  HEADER: HeaderBlock,
  STUDENT_INFO: StudentInfoBlock,
  SCHOLASTIC: ScholasticBlock,
  CO_SCHOLASTIC: CoScholasticBlock,
  FOOTER: FooterBlock,
  SIGNATURES: SignaturesBlock,
  GRADE_LEGEND: LegendBlock,
  TEXT: TextBlock,
  SPACER: SpacerBlock,
};

/* ------------------------------------------------------------------ card */

const ReportCardView = ({ report, school = {}, layout: layoutProp, highlightId }) => {
  if (!report) return null;
  const layout = layoutProp || report.layout || DEFAULT_LAYOUT;
  const settings = { ...DEFAULT_LAYOUT.settings, ...(layout.settings || {}) };

  return (
    <div className="rc-page" style={{ fontSize: `${settings.font_size || 11}px` }}>
      {(layout.blocks || []).filter((b) => b.visible !== false).map((b) => {
        const R = RENDERERS[b.type];
        if (!R) return null;
        return (
          <div key={b.id} className={`rc-block${highlightId === b.id ? " rc-highlight" : ""}`}>
            <R report={report} school={school} settings={settings} block={b} />
          </div>
        );
      })}
      {report.version && <div className="rc-version">v{report.version}</div>}
    </div>
  );
};

export default ReportCardView;
